import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Smart Template Engine — template instances (bind a theme + menu + per-screen
 * layout) and the built-in theme catalogue. Backend TemplateInstanceController /
 * SignageThemeController (.claude/TEMPLATE_ENGINE_SPEC.md).
 *
 *   GET    /signage-templates        → { templates }
 *   POST   /signage-templates        create (name, type, theme_key, menu_id,
 *                                            screen_assignments, settings)
 *   GET    /signage-templates/{id}    → { template } (+ menu.sections.items)
 *   PUT    /signage-templates/{id}    update
 *   DELETE /signage-templates/{id}
 *   GET    /signage-themes            → { themes, default_zones }
 *
 * Resource-wrapped responses ({ data: { status, ... } }) — unwrap `res?.data ?? res`.
 */

const LIST_KEY = 'signageTemplates';
const itemKey = (id) => ['signageTemplate', id];
const THEMES_KEY = 'signageThemes';

export const useGetTemplates = () =>
  useQuery(
    LIST_KEY,
    async () => {
      const res = (await axiosInstance.get('/signage-templates')).data;
      return (res?.data ?? res)?.templates ?? [];
    },
    { staleTime: 15_000 }
  );

export const useGetTemplate = (id, enabled = true) =>
  useQuery(
    itemKey(id),
    async () => {
      const res = (await axiosInstance.get(`/signage-templates/${id}`)).data;
      return (res?.data ?? res)?.template ?? null;
    },
    { enabled: !!id && enabled, staleTime: 5_000 }
  );

/** Built-in theme catalogue (code constants) + default zones, for the editor. */
export const useGetThemes = () =>
  useQuery(
    THEMES_KEY,
    async () => {
      const res = (await axiosInstance.get('/signage-themes')).data;
      const body = res?.data ?? res;
      return {
        themes: body?.themes ?? [],
        defaultZones: body?.default_zones ?? {},
        defaultZonesPortrait: body?.default_zones_portrait ?? body?.default_zones ?? {},
      };
    },
    { staleTime: 60 * 60_000 } // themes are static
  );

export const useCreateTemplate = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/signage-templates', payload), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      showToast('success', 'Templates', 'Template created.');
    },
    onError: (err) => showToast('error', 'Templates', err.response?.data?.message),
  });
};

/** 30-second Daily Special — atomically creates a 1-item menu + special instance. */
export const useQuickSpecial = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/signage-templates/quick-special', payload), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      qc.invalidateQueries('signageMenus');
      showToast('success', 'Daily Special', 'Special created.');
    },
    onError: (err) => showToast('error', 'Daily Special', err.response?.data?.message),
  });
};

export const useUpdateTemplate = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, ...payload }) => axiosInstance.put(`/signage-templates/${id}`, payload), {
    onSuccess: (_res, vars) => {
      qc.invalidateQueries(LIST_KEY);
      qc.invalidateQueries(itemKey(vars.id));
      showToast('success', 'Templates', 'Template saved.');
    },
    onError: (err) => showToast('error', 'Templates', err.response?.data?.message),
  });
};

export const useDeleteTemplate = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/signage-templates/${id}`), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      showToast('info', 'Templates', 'Template deleted.');
    },
    onError: (err) => showToast('error', 'Templates', err.response?.data?.message),
  });
};
